import React, { useEffect, useState } from "react";

import styled from "styled-components/macro";

import useEmailOnly from "FamilyRegistration/useEmailOnly";
import API from "UserProfile/Family/API";

import FnrDateInput from "UI/FnrDateInput";
import FnrInput from "UI/FnrInput";
import { FnrOverlayTitle } from "UI/FnrOverlay";

import { formatPhoneNumber } from "Utilities/Number";

import GenderToggle from "./GenderToggle";

export default function AddFamilyMemberByName({
  profileUserID,
  lastName = "",
  shouldSave,
  loading,
  setLoading,
  setSaveDisabled,
  setRelationshipUser,
  setAvailableRelationships,
  setCreatedHouseholdID,
  setShouldSave,
  setShouldShow,
  closeOverlay,
  groupID,
  setAlertMsg,
  setShowAlert,
}) {
  const [userFirstName, setUserFirstName] = useState("");
  const [userLastName, setUserLastName] = useState(lastName);
  const [userEmail, setUserEmail] = useState("");
  const [userPhone, setUserPhone] = useState("");
  const [userGender, setUserGender] = useState(null);
  const [userBirthday, setUserBirthday] = useState({});

  const { emailOnly } = useEmailOnly();

  useEffect(() => {
    setSaveDisabled(userFirstName.length < 2);

    // Aug 2021 → we’re doing a clean slate of all warnings and ain’t got time to look at this so that’s why this is disabled
    // eslint-disable-next-line
  }, [userFirstName]);

  useEffect(() => {
    if (!shouldSave || loading) return;

    setLoading(true);

    const createNewUser = API.createNewUser(groupID, {
      profileUserID,
      fname: userFirstName,
      lname: userLastName,
      email: userEmail,
      mobile_phone: userPhone,
      gender: userGender || "",
      birthdayMonth: userBirthday.month || "",
      birthdayDay: userBirthday.day || "",
      birthdayYear: userBirthday.year || "",
    });

    createNewUser
      .post()
      .then(
        ({
          relationshipUser,
          relationships,
          shouldAskForInfo,
          envelopeNumberConflict,
          householdID,
        }) => {
          //refresh everyone group people page
          const path = window.location.pathname.split("/");
          if (path[1] === "everyone" && path[2] === "people")
            if (window.fnReactBus.forceBackboneRefresh) {
              window.fnReactBus.forceBackboneRefresh("people");
            }
          setLoading(false);
          setShouldSave(false);
          if (envelopeNumberConflict) {
            setShouldShow(false);
            alert(
              "It looks like both of these people have different envelope numbers.  You'll need to delete the old envelope number from one of their profiles before adding them into the same household."
            );
            closeOverlay();
          } else {
            setCreatedHouseholdID(householdID);
            setRelationshipUser(relationshipUser);
            relationships.suggested.push({
              ID: 0,
            });
            setAvailableRelationships(relationships);
          }
        }
      )
      .catch((e) => {
        setLoading(false);
        if (e.error === "same_user") {
          setAlertMsg(
            "Please verify the new member has unique contact info and try again."
          );
        } else if (e.error === "no_admin") {
          setAlertMsg(
            "You do not have permission to add this person to the household. Please contact your network administrator."
          );
        } else {
          setAlertMsg(
            "Oh no! There was a problem adding this family member. Please try again later."
          );
        }
        setShowAlert(true);
        setShouldSave(false);
      });

    return () => createNewUser.cancel();

    // Aug 2021 → we’re doing a clean slate of all warnings and ain’t got time to look at this so that’s why this is disabled
    // eslint-disable-next-line
  }, [shouldSave]);

  return (
    <Wrapper>
      <FnrOverlayTitle style={{ border: "none" }}>
        Add a Family Member
      </FnrOverlayTitle>
      <AddUserNameHolder>
        <FnrInput
          placeholder="First Name"
          value={userFirstName}
          onChange={setUserFirstName}
          disabled={loading}
          size="large"
          style={{
            flexGrow: 0,
            flexShrink: 0,
            width: "calc(50% - 6px)",
            marginRight: 12,
          }}
        />
        <FnrInput
          placeholder="Last Name"
          value={userLastName}
          onChange={setUserLastName}
          disabled={loading}
          size="large"
          style={{ flexGrow: 0, flexShrink: 0, width: "calc(50% - 6px)" }}
        />
      </AddUserNameHolder>
      {!emailOnly && (
        <FnrInput
          placeholder="Cell Phone"
          value={userPhone}
          disabled={loading}
          onChange={(value) => setUserPhone(formatPhoneNumber(value))}
          style={{ marginTop: 12 }}
        />
      )}
      <FnrInput
        placeholder="Email Address"
        value={userEmail}
        disabled={loading}
        onChange={setUserEmail}
        style={{ marginTop: 12 }}
      />
      <AddUserInfoSection>
        <GenderToggle
          gender={userGender}
          setGender={setUserGender}
          style={{ flexShrink: 2 }}
        />
        <FnrDateInput
          style={{ flexGrow: 2 }}
          placeholder="Birthday"
          setDate={setUserBirthday}
        />
      </AddUserInfoSection>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  width: max-content;
  max-width: 440px;
  box-sizing: border-box;

  @media screen and (max-width: 500px) {
    width: 325px;
  }
  @media screen and (max-width: 400px) {
    width: 83vw;
  }
`;
const AddUserNameHolder = styled.div`
  display: flex;
`;

const AddUserInfoSection = styled.div`
  display: flex;
  margin-top: 12px;
  justify-content: space-between;
  gap: 12px;

  @media screen and (max-width: 500px) {
    flex-direction: column;
  }
`;
